import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { CmsInfoPagesEditor } from "@/components/admin/cms-info-pages-editor";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import { siteApi } from "@/lib/site/site-api";

/**
 * Les pages éditables, et leur nom lisible.
 *
 * Les clés sont celles acceptées par l'API (`SITE_PAGE_KEYS`) : la page d'aide
 * s'y appelle « faq », pas « help ».
 */
const PAGES: Array<{ key: string; label: string }> = [
	{ key: "about", label: "À propos" },
	{ key: "faq", label: "Aide" },
	{ key: "made-in-cameroon", label: "Made in Cameroon" },
	{ key: "cgv", label: "Conditions de vente" },
];

const dateHeure = (iso: string) =>
	new Date(iso).toLocaleString("fr-FR", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

/**
 * Pages d'information : à propos, aide, made in Cameroon, conditions de vente.
 *
 * L'éditeur est conservé tel quel — c'est le plus gros contenu du CMS et le
 * réécrire aurait mis en jeu des dizaines de champs sans bénéfice immédiat.
 *
 * S'y ajoute l'historique des versions. Chaque enregistrement archivait déjà
 * la version précédente, mais rien ne permettait de la relire ni de revenir
 * en arrière : sur des conditions de vente, c'est précisément ce dont on a
 * besoin le jour où une modification pose problème.
 */
export function AdminCmsPagesPage() {
	const [historique, setHistorique] = useState<{
		key: string;
		label: string;
	} | null>(null);

	return (
		<>
			<PageHead
				title="Pages d’information"
				sub="Contenus des pages À propos, Aide, Made in Cameroon et CGV"
				actions={
					<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
						{PAGES.map((p) => (
							<AdminButton
								key={p.key}
								variant="ghost"
								size="sm"
								onClick={() => setHistorique(p)}
							>
								<AdminIcon n="refresh" s={12} />
								Versions · {p.label}
							</AdminButton>
						))}
					</div>
				}
			/>
			<CmsInfoPagesEditor />

			{historique && (
				<HistoriqueModal
					pageKey={historique.key}
					label={historique.label}
					onClose={() => setHistorique(null)}
				/>
			)}
		</>
	);
}

/**
 * Historique d'une page.
 *
 * Restaurer archive d'abord la version en ligne : revenir en arrière ne fait
 * donc jamais perdre ce qui était publié, et une restauration malheureuse se
 * défait comme n'importe quelle autre modification.
 */
function HistoriqueModal({
	pageKey,
	label,
	onClose,
}: {
	pageKey: string;
	label: string;
	onClose: () => void;
}) {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { hasAny, isSuperAdmin } = usePermissions();
	const peutEcrire = isSuperAdmin || hasAny("MARKETING.CMS.WRITE");

	const { data, isLoading } = useQuery({
		queryKey: ["site-page-revisions", pageKey],
		queryFn: () => siteApi.pageRevisions(pageKey),
	});

	const restaurer = useMutation({
		mutationFn: (id: string) => siteApi.restorePageRevision(pageKey, id),
		onSuccess: () => {
			toast.success("Version restaurée");
			void qc.invalidateQueries({ queryKey: ["site-page"] });
			void qc.invalidateQueries({ queryKey: ["site-page-revisions", pageKey] });
			onClose();
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const versions = data?.items ?? [];

	return (
		<Modal
			title={`Versions · ${label}`}
			subtitle={
				isLoading
					? "Chargement…"
					: `${data?.total ?? 0} version(s) archivée(s)`
			}
			onClose={onClose}
			width={560}
		>
			{isLoading ? (
				<div className="empty">Chargement…</div>
			) : versions.length === 0 ? (
				<div className="empty">
					Aucune version antérieure. La première sera archivée au prochain
					enregistrement de cette page.
				</div>
			) : (
				<div>
					{versions.map((v, i) => (
						<div key={v.id} className={`ver-row${i === 0 ? " current" : ""}`}>
							<span className="ver-dot" />
							<div style={{ flex: 1, minWidth: 0 }}>
								<strong style={{ fontSize: 12.5 }}>
									{dateHeure(v.createdAt)}
									{i === 0 && (
										<span
											className="muted"
											style={{ fontWeight: 400, marginLeft: 6 }}
										>
											— la plus récente avant celle en ligne
										</span>
									)}
								</strong>
								<div className="muted" style={{ fontSize: 11 }}>
									{v.action === "UPDATE" ? "Modification" : v.action}
								</div>
							</div>
							{peutEcrire && (
								<AdminButton
									variant="ghost"
									size="sm"
									disabled={restaurer.isPending}
									onClick={() => {
										void confirm({
											title: "Restaurer cette version ?",
											message: `Le contenu du ${dateHeure(v.createdAt)} remplacera celui en ligne. La version actuelle sera archivée, rien n'est perdu.`,
											confirmLabel: "Restaurer",
											tone: "default",
										}).then((ok) => {
											if (ok) restaurer.mutate(v.id);
										});
									}}
								>
									Restaurer
								</AdminButton>
							)}
						</div>
					))}
				</div>
			)}
			<ModalFooter>
				<AdminButton variant="primary" onClick={onClose}>
					Fermer
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
